// ChoosePlatformPage — Platform picker with tier-gated features
// Replaces the stock picker. Shows trading platforms (Deepchart, ATAS, etc.)
// with the user's current tier and which platforms are locked behind upgrades.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PLATFORMS, TIERS, TIERS as TIERS_MAP, type TierKey, BOT_STRATEGIES } from '../../data/platforms';
import { PlatformLogo, TierPill } from '../../components/Brand';
import { DEMO_CHALLENGE } from '../../data/platforms';

function genSpark(seed: number, n: number, trend: number): number[] {
  const out: number[] = [];
  let v = 100 + seed * 5;
  for (let i = 0; i < n; i++) {
    v += trend + (Math.random() - 0.5) * 4;
    out.push(v);
  }
  return out;
}

export function ChooseStockPage() {
  const nav = useNavigate();
  const userTier: TierKey = DEMO_CHALLENGE.tier;
  const [q, setQ] = useState('');

  const tierOrder = { Beginner: 0, Mid: 1, Professional: 2 } as const;

  const popular = useMemo(() => PLATFORMS.filter((p) => p.badge || p.tierRequired === 'Beginner').slice(0, 3), []);
  const pro = useMemo(() => PLATFORMS.filter((p) => p.tierRequired !== 'Beginner'), []);
  const all = useMemo(() => PLATFORMS, []);

  function isLocked(p: typeof PLATFORMS[0]) {
    return tierOrder[userTier] < tierOrder[p.tierRequired];
  }

  function PlatformRow({ platform, seed = 0, trend = 0 }: { platform: typeof PLATFORMS[0]; seed?: number; trend?: number }) {
    const locked = isLocked(platform);
    return (
      <button
        onClick={() => !locked && nav('/portal/platform/' + platform.key)}
        data-testid={`platform-row-${platform.key}`}
        className={['flex w-full items-center gap-3 border-b border-tf-border bg-white px-4 py-3 text-left last:border-b-0',
          locked ? 'opacity-60' : 'active:bg-tf-surface-1'].join(' ')}
      >
        <PlatformLogo name={platform.key} size={40} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[15px] font-semibold text-tf-text-1">{platform.displayName}</span>
            {platform.badge && <span className="rounded-full bg-tf-warning px-1.5 py-0.5 text-[9px] font-semibold uppercase text-white">{platform.badge}</span>}
            {locked && <span className="rounded-full bg-tf-text-1 px-1.5 py-0.5 text-[9px] font-semibold text-white">🔒 {platform.tierRequired}</span>}
          </div>
          <div className="truncate text-[12px] text-tf-text-3">{platform.description}</div>
        </div>
        <div className="h-8 w-16 shrink-0">
          <MiniSpark points={genSpark(seed, 18, trend)} color="#34C759" />
        </div>
        <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0 text-tf-text-muted" fill="none" stroke="currentColor" strokeWidth="2">
          {locked ? (
            <><rect x="6" y="11" width="12" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" strokeLinecap="round" /></>
          ) : (
            <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
      </button>
    );
  }

  return (
    <div className="bg-white min-h-screen">
      {/* Top nav */}
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-white/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold"></div>
        <div className="w-9" />
      </div>

      {/* Header with tier badge */}
      <div className="px-4 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-[8px] bg-tf-blue-tint text-tf-blue">
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8">
                <path d="M3 7l9-4 9 4-9 4-9-4z" /><path d="M3 12l9 4 9-4M3 17l9 4 9-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <h1 className="text-[20px] font-bold tracking-tight text-tf-text-1">Choose a platform</h1>
              <p className="text-[12px] text-tf-text-3">License included for the duration of your active challenge.</p>
            </div>
          </div>
          <TierPill tier={userTier} />
        </div>
      </div>

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="flex items-center gap-2 rounded-[10px] bg-tf-surface-1 px-3 py-2">
          <svg viewBox="0 0 24 24" className="h-4 w-4 text-tf-text-3" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search platforms"
            data-testid="platform-search"
            className="flex-1 bg-transparent text-[15px] text-tf-text-1 outline-none placeholder:text-tf-text-3"
          />
        </div>
      </div>

      {/* List sections */}
      <div className="mt-2">
        {q.trim() === '' ? (
          <>
            <SectionHeader>Popular</SectionHeader>
            <div className="bg-white">
              {popular.map((p, i) => <PlatformRow key={p.key} platform={p} seed={i} trend={1} />)}
            </div>
            <SectionHeader>Pro platforms</SectionHeader>
            <div className="bg-white">
              {pro.map((p, i) => <PlatformRow key={p.key} platform={p} seed={i + 4} trend={0.5} />)}
            </div>
            {all.length > 0 && (
              <>
                <SectionHeader>All platforms</SectionHeader>
                <div className="bg-white">
                  {all.map((p, i) => <PlatformRow key={p.key} platform={p} seed={i + 10} trend={0.4} />)}
                </div>
              </>
            )}
            {/* Upgrade CTA */}
            <div className="px-4 mt-4">
              <button
                onClick={() => nav('/portal/tiers')}
                className="w-full rounded-[16px] bg-gradient-to-br from-[#1C1C1E] to-[#2A2A2A] p-4 text-left text-white active:scale-[0.99]"
              >
                <div className="text-[15px] font-semibold">Unlock all platforms & strategies</div>
                <div className="mt-1 text-[12px] text-white/70">Upgrade to Mid or Professional tier to access all 7 platforms and AI bot strategies.</div>
                <div className="mt-2 text-[12px] font-semibold text-tf-profit">Compare tiers →</div>
              </button>
            </div>
          </>
        ) : (
          <div className="bg-white">
            {PLATFORMS.filter((p) => p.displayName.toLowerCase().includes(q.toLowerCase()) || p.key.toLowerCase().includes(q.toLowerCase()))
              .map((p, i) => <PlatformRow key={p.key} platform={p} seed={i} trend={0.5} />)}
            {PLATFORMS.filter((p) => p.displayName.toLowerCase().includes(q.toLowerCase()) || p.key.toLowerCase().includes(q.toLowerCase())).length === 0 && (
              <div className="p-8 text-center text-[14px] text-tf-text-3">No platforms match "{q}"</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 pt-6 pb-2 text-[13px] font-normal uppercase tracking-wide text-tf-text-3">
      {children}
    </div>
  );
}

function MiniSpark({ points, color = '#34C759' }: { points: number[]; color?: string }) {
  if (points.length < 2) return null;
  const w = 64, h = 32;
  const min = Math.min(...points), max = Math.max(...points) - min || 1;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${(i / (points.length - 1)) * w} ${h - ((p - min) / max) * (h - 4) - 2}`).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="h-full w-full">
      <path d={path} fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
